// crm-deal-lifecycle.ts — TRACK C: the CRM DEAL-STAGE lifecycle DEFINITION.
//
// Deals are the one owner-managed CRM collection with a real pipeline state machine
// (Lead → Qualified → Proposal → Won | Lost) but, unlike bookings/orders/tickets, the deals
// contract shipped with a `stage` statusField and NO lifecycle — so the generic owner route rejects
// a post-create stage move (PATCH { status } → 400 no_status_lifecycle) and the console could only
// set the stage at CREATE. This module supplies the missing piece: a StatusLifecycle for `deal`,
// shaped EXACTLY like status-lifecycle.ts's ORDER_LIFECYCLE so the integrator can fold it into the
// LIFECYCLES registry and `getLifecycle('deal')` resolves — at which point the SAME server validation
// (updateOwnerRecord's applyTransition) enforces valid-only owner transitions with no new server code.
//
// SELF-CONTAINED: the only dependency is a TYPE-ONLY import of the shared lifecycle shapes (erased at
// runtime), so the module carries no runtime coupling to the registry. Its own pure helpers
// (dealAllowedNext / isValidDealTransition / applyDealTransition) implement the transition algorithm
// directly on DEAL_LIFECYCLE, so the definition is provable in isolation.
//
// BACKWARD-COMPAT (load-bearing): the state ids are the EXISTING capitalized stage values already
// stored by every surface — ManageCRM's STAGES, the generation-worker crm/smartcrm recipes'
// DEAL_STAGES, compose-categories' STAGE_OPTS, and spec-to-atlas sample data all use
// 'Lead'/'Qualified'/'Proposal'/'Won'/'Lost'. Any already-persisted deal therefore resolves in the
// lifecycle; a lowercase convention (like the other lifecycles) would make applyTransition reject
// every existing row as unknown_from. Owner-only: deals are owner-only business data — a customer
// never reads or moves them, so every transition is `by: ['owner']`.
// §dependency direction — DEAL_LIFECYCLE is DEFINED in the canonical status-lifecycle layer (the
// registry owns it); this feature module CONSUMES it (feature→shared: correct direction, no inversion,
// no runtime cycle) and adds the CRM-specific derived constants + pure helpers. It does NOT re-export
// DEAL_LIFECYCLE (that would collide with the shared barrel's status-lifecycle export). One source of truth.
import { DEAL_LIFECYCLE } from './status-lifecycle';
import type { LifecycleActor, StatusEvent, TransitionResult } from './status-lifecycle';

/** The ordered pipeline stages — DERIVED from the canonical DEAL_LIFECYCLE states, so the stage-id list
 *  has a single source of truth (no duplicated tuple that could drift from the lifecycle object). */
export const DEAL_STAGES: readonly string[] = DEAL_LIFECYCLE.states.map((s) => s.id);
export type DealStage = string;

/** The stage a deal starts at when none is supplied (the lifecycle initial). */
export const DEAL_INITIAL_STAGE: DealStage = DEAL_LIFECYCLE.initial;

/** Terminal stages — a closed deal has no outgoing transitions (derived from the lifecycle). */
export const DEAL_TERMINAL_STAGES: readonly string[] = DEAL_LIFECYCLE.states.filter((s) => s.terminal).map((s) => s.id);

/** Stages that render with danger styling in the transition control (closing a deal LOST). A
 *  UI-presentation choice — not part of the lifecycle machine — so it stays a literal here. */
export const DEAL_DANGER_STAGES = ['Lost'] as const;

// ── CRM-specific pure helpers (operate on the imported canonical DEAL_LIFECYCLE) ────────────────────

function hasStage(id: string): boolean {
  return DEAL_LIFECYCLE.states.some((s) => s.id === id);
}

/** True when `id` is a terminal (closed) stage. */
export function isTerminalDealStage(id: string): boolean {
  const s = DEAL_LIFECYCLE.states.find((x) => x.id === id);
  return !!s && s.terminal;
}

function actorAllowed(by: readonly LifecycleActor[], actor: LifecycleActor | undefined): boolean {
  if (!actor || actor === 'any') return true;
  if (by.includes('any')) return true;
  return by.includes(actor);
}

/** The stages reachable from `from` (optionally restricted to `actor`, default 'owner'). Valid-only —
 *  this is what the E0 StateTransitionBar derives its buttons from, so an illegal edge is never
 *  offered. A terminal or unknown `from` yields []. */
export function dealAllowedNext(from: string, actor: LifecycleActor = 'owner'): string[] {
  return DEAL_LIFECYCLE.transitions
    .filter((tr) => tr.from === from && actorAllowed(tr.by, actor))
    .map((tr) => tr.to);
}

/** Can `from → to` happen for `actor` (default 'owner')? Same edge check the server applies. */
export function isValidDealTransition(from: string, to: string, actor: LifecycleActor = 'owner'): boolean {
  if (from === to) return false;
  if (!hasStage(from) || !hasStage(to)) return false;
  const tr = DEAL_LIFECYCLE.transitions.find((x) => x.from === from && x.to === to);
  if (!tr) return false;
  return actorAllowed(tr.by, actor);
}

/** Validate + describe a deal stage transition, returning the audit event (actor identity + timestamp)
 *  to append when ok. `now` is injected (deterministic — no Date.now in shared, matching
 *  status-lifecycle.applyTransition). An invalid/forbidden/terminal move returns { ok:false } with the
 *  reason — never a corrupt "success". */
export function applyDealTransition(
  from: string,
  to: string,
  actor: LifecycleActor,
  now: number
): TransitionResult {
  if (!hasStage(from)) return { ok: false, error: 'unknown_from' };
  if (!hasStage(to)) return { ok: false, error: 'unknown_to' };
  if (isTerminalDealStage(from)) return { ok: false, error: 'terminal' };
  const tr = DEAL_LIFECYCLE.transitions.find((x) => x.from === from && x.to === to);
  if (!tr) return { ok: false, error: 'illegal_transition' };
  if (!actorAllowed(tr.by, actor)) return { ok: false, error: 'forbidden_actor' };
  const event: StatusEvent = { from, to, by: actor, at: now };
  return { ok: true, status: to, event };
}
